/**
 * CAR-IA Design Tokens — v3 (typed mirror of commodity/styles/theme.css)
 *
 * Single typed source of truth for the CAR-IA product baseline, extracted
 * from the v3.x HTML prototypes (docs/html-prototypes/). Forest-green brand,
 * warm off-white surfaces, ink text hierarchy, trust/decision/lead semantics,
 * Inter (UI) + JetBrains Mono (numerics).
 *
 * Every `cssVar` resolves against the custom properties in theme.css, so JS
 * consumers (charts, inline styles) and CSS stay in lockstep. `value` is the
 * resolved hex for the rare case a raw color is needed (e.g. canvas/chart).
 */

/** Spacing scale (mirrors --spacing-* in theme.css). rem strings. */
export const spacing = {
  xs: '0.25rem',
  sm: '0.5rem',
  md: '1rem',
  lg: '1.5rem',
  xl: '2rem',
  '2xl': '3rem',
  '3xl': '4rem',
} as const;

/** Typography (mirrors --font-* in theme.css). */
export const typography = {
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  /** Tabular numerics — prices, mileage, IDs. Use `.mono` / `.num` in CSS. */
  fontFamilyMono: "'JetBrains Mono', 'SFMono-Regular', Menlo, Monaco, monospace",
  size: {
    xs: '0.75rem',
    sm: '0.875rem',
    base: '1rem',
    lg: '1.125rem',
    xl: '1.25rem',
    '2xl': '1.5rem',
    '3xl': '1.875rem',
    '4xl': '2.25rem',
    '5xl': '3rem',
  },
  weight: { normal: 400, medium: 500, semibold: 600, bold: 700, extrabold: 800, black: 900 },
  lineHeight: { tight: 1.25, normal: 1.5, relaxed: 1.75 },
} as const;

/** Mobile-first breakpoints (px). Buyer feed/detail are mobile-first. */
export const breakpoints = { sm: 640, md: 768, lg: 1024, xl: 1280 } as const;

/** Media-query helper: `@media ${mq.md} { ... }` (min-width, mobile-first). */
export const mq = {
  sm: `(min-width: ${breakpoints.sm}px)`,
  md: `(min-width: ${breakpoints.md}px)`,
  lg: `(min-width: ${breakpoints.lg}px)`,
  xl: `(min-width: ${breakpoints.xl}px)`,
} as const;

/** Border radius (mirrors --radius-* in theme.css). */
export const radius = {
  xs: '0.375rem',
  sm: '0.5rem',
  md: '0.625rem',
  lg: '0.75rem',
  xl: '1.125rem',
  '2xl': '1.75rem',
  full: '9999px',
} as const;

/** Shadow tokens (mirror --sh-* in theme.css). */
export const shadow = {
  sh1: 'var(--sh-1)',
  sh2: 'var(--sh-2)',
  card: 'var(--sh-card)',
  cardStrong: 'var(--sh-card-strong)',
  premium: 'var(--sh-premium)',
} as const;

/** Surfaces & ink (text) hierarchy. */
export const surfaces = {
  page: { value: '#FAFAF7', cssVar: 'var(--surf-page)' },
  card: { value: '#FFFFFF', cssVar: 'var(--surf-card)' },
  tint: { value: '#F5F4EF', cssVar: 'var(--surf-tint)' },
  deep: { value: '#1A2330', cssVar: 'var(--surf-deep)' },
  topbar: { value: '#0F1B2D', cssVar: 'var(--topbar)' },
} as const;

export const ink = {
  1: { value: '#0A1628', cssVar: 'var(--ink-1)' },
  2: { value: '#1F2937', cssVar: 'var(--ink-2)' },
  3: { value: '#4A5568', cssVar: 'var(--ink-3)' },
  4: { value: '#6B7280', cssVar: 'var(--ink-4)' },
  5: { value: '#9CA3AF', cssVar: 'var(--ink-5)' },
} as const;

/**
 * CAR-IA semantic palette (v3). `cssVar` is what components should consume.
 */
export const palette = {
  brand: { value: '#0A4A2A', cssVar: 'var(--brand)' },
  brand2: { value: '#1A6E42', cssVar: 'var(--brand-2)' },

  // Trust signals (verified marks on cards + detail).
  trustPositive: { value: '#0F5E4A', cssVar: 'var(--trust)' }, // verified — green
  trustNeutral: { value: '#9CA3AF', cssVar: 'var(--ink-5)' }, // unknown — gray
  trustNegative: { value: '#9B2C4A', cssVar: 'var(--hot)' }, // failed — red

  // Decision chips (price delta vs. suggested price).
  decisionPositive: { value: '#2D7A52', cssVar: 'var(--good)' }, // good deal — green
  decisionNeutral: { value: '#6B7280', cssVar: 'var(--ink-4)' }, // fair — gray
  decisionNegative: { value: '#9A6017', cssVar: 'var(--warn)' }, // overpriced — amber

  decision: { value: '#2557A7', cssVar: 'var(--decision)' }, // analytic blue
  premium: { value: '#B8860B', cssVar: 'var(--premium)' }, // gold
  hot: { value: '#9B2C4A', cssVar: 'var(--hot)' },
  warn: { value: '#9A6017', cssVar: 'var(--warn)' },
  good: { value: '#2D7A52', cssVar: 'var(--good)' },
} as const;

/** Lead tiers (seller dashboards). */
export const leadTier = {
  hot: { value: '#B8423A', cssVar: 'var(--lead-hot)', bg: 'var(--lead-hot-bg)' },
  medium: { value: '#9A6017', cssVar: 'var(--lead-medium)', bg: 'var(--lead-medium-bg)' },
  cold: { value: '#4A5568', cssVar: 'var(--lead-cold)', bg: 'var(--lead-cold-bg)' },
} as const;

/** Trust-signal state → token (used by TrustChip / TrustMark / TrustSection). */
export const trustStateToken = {
  verified: { label: 'Verificado', color: palette.trustPositive.cssVar, bg: 'var(--trust-bg)' },
  pending: { label: 'Pendiente', color: palette.trustNeutral.cssVar, bg: 'var(--surf-tint)' },
  unknown: { label: 'Sin verificar', color: palette.trustNeutral.cssVar, bg: 'var(--surf-tint)' },
  failed: { label: 'No verificado', color: palette.trustNegative.cssVar, bg: 'var(--hot-bg)' },
} as const;

/** Price-delta verdict → token (used by DecisionChip). */
export const decisionToken = {
  'good-deal': {
    label: 'Buen precio',
    color: palette.decisionPositive.cssVar,
    bg: 'var(--good-bg)',
  },
  'fair-deal': {
    label: 'Precio justo',
    color: palette.decisionNeutral.cssVar,
    bg: 'var(--neutral-bg)',
  },
  overpriced: {
    label: 'Sobreprecio',
    color: palette.decisionNegative.cssVar,
    bg: 'var(--warn-bg)',
  },
} as const;

/** Listing lifecycle status → token (used by StatusChip). */
export const statusToken = {
  draft: { label: 'Borrador', color: palette.trustNeutral.cssVar, bg: 'var(--surf-tint)' },
  active: { label: 'Activo', color: palette.trustPositive.cssVar, bg: 'var(--trust-bg)' },
  paused: { label: 'Pausado', color: palette.warn.cssVar, bg: 'var(--warn-bg)' },
  archived: { label: 'Archivado', color: palette.trustNeutral.cssVar, bg: 'var(--neutral-bg)' },
  suspended: { label: 'Suspendido', color: palette.trustNegative.cssVar, bg: 'var(--hot-bg)' },
} as const;

/** Lead tier → token (used by LeadChip). */
export const leadToken = {
  hot: { label: 'HOT', color: leadTier.hot.cssVar, bg: leadTier.hot.bg },
  medium: { label: 'MEDIUM', color: leadTier.medium.cssVar, bg: leadTier.medium.bg },
  cold: { label: 'COLD', color: leadTier.cold.cssVar, bg: leadTier.cold.bg },
} as const;

export type TrustState = keyof typeof trustStateToken;
export type DecisionVerdict = keyof typeof decisionToken;
export type ListingStatus = keyof typeof statusToken;
export type LeadTier = keyof typeof leadToken;

export const designTokens = {
  spacing,
  typography,
  breakpoints,
  mq,
  radius,
  shadow,
  surfaces,
  ink,
  palette,
  leadTier,
  trustStateToken,
  decisionToken,
  statusToken,
  leadToken,
};

export default designTokens;
